import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import * as customItemGroupActions from '@redux/modules/customItemGroup'

import defaultFormState, { requiredFields } from '@models/customItemGroup'

import PageContext from '@contexts/pageContext'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams, ModuleState } from '@redux/modules/types'
import type { CustomItemGroupModel, CustomItemGroupRequestOptions } from '@models/types'

export const generateUrls = (customItemGroup?: Partial<CustomItemGroupModel>) => {
  const { id } = customItemGroup || {}

  return {
    editCustomItemGroupDetailsUrl: `#/systemManager/customItemGroups/${id}/edit/details`,
    editCustomItemGroupUrl: `#/systemManager/customItemGroups/${id}/edit/`,
    customItemGroupsIndexUrl: '#/systemManager/customItemGroups',
  }
}

type CreateCustomItemGroupParams = {
  customItemGroupParams: Partial<CustomItemGroupModel>,
  dispatch: AppDispatch,
  requestOptions?: CustomItemGroupRequestOptions,
}

const createCustomItemGroup = (params: CreateCustomItemGroupParams) => {
  const { dispatch, customItemGroupParams, requestOptions } = params
  const { createCustomItemGroup: createFn } = customItemGroupActions

  return dispatch(createFn(customItemGroupParams, requestOptions))
}

type UpdateCustomItemGroupParams = {
  customItemGroup: CustomItemGroupModel,
  customItemGroupParams: Partial<CustomItemGroupModel>,
  dispatch: AppDispatch,
  requestOptions?: CustomItemGroupRequestOptions,
}

const updateCustomItemGroup = (params: UpdateCustomItemGroupParams) => {
  const {
    dispatch, customItemGroup, customItemGroupParams, requestOptions,
  } = params
  const { updateCustomItemGroup: updateFn } = customItemGroupActions

  const updatedParams = {
    id: customItemGroup.id,
    ...customItemGroupParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteCustomItemGroupParams = {
  customItemGroup: DeleteParams<CustomItemGroupModel>,
  dispatch: AppDispatch,
}

const deleteCustomItemGroup = (params: DeleteCustomItemGroupParams) => {
  const { dispatch, customItemGroup } = params
  const { deleteCustomItemGroup: deleteFn } = customItemGroupActions

  return dispatch(deleteFn(customItemGroup))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useCustomItemGroupForm(
  customItemGroup: Partial<CustomItemGroupModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const customItemGroupForm = useForm(
    defaultFormState,
    { entity: customItemGroup, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [customItemGroup.id, customItemGroup.cache_key],
  )

  return {
    ...customItemGroupForm,
  }
}

export const useRelations = (customItemGroup: Partial<CustomItemGroupModel> = {}) => {
  // @ts-expect-error TS2339
  const { related_entity_id } = customItemGroup

  // @ts-expect-error TS2339
  const { relatedEntities } = useSelector(reduxState => reduxState.entities)

  const relatedEntity = related_entity_id && relatedEntities[related_entity_id] ? relatedEntities[related_entity_id] : {}

  return {
    relatedEntity,
  }
}

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: CustomItemGroupRequestOptions,
}

function useCustomItemGroup(initEntity: Partial<CustomItemGroupModel> = {}, options: Options = {}) {
  const { performHttpRequests, requestOptions } = options

  // @ts-expect-error TS2322
  const { entity: customItemGroup }: { entity: CustomItemGroupModel} = useLatestEntity(initEntity, 'customItemGroups')
  const { id } = customItemGroup

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const { relatedEntity } = useRelations(customItemGroup)

  useReduxAction(
    'customItemGroups',
    'loadCustomItemGroup',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const {
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.customItemGroups)

  return {
    callbacks: {
      createCustomItemGroup: (customItemGroupParams: Partial<CustomItemGroupModel>, entityOptions?: CustomItemGroupRequestOptions) => (
        createCustomItemGroup({ customItemGroupParams, dispatch, requestOptions: entityOptions })
      ),
      createOrEditCustomItemGroup: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditCustomItemGroupModal',
        payload: { customItemGroup },
      }),
      deleteCustomItemGroup: () => deleteCustomItemGroup({ dispatch, customItemGroup }),
      updateCustomItemGroup: (customItemGroupParams: Partial<CustomItemGroupModel>, entityOptions?: CustomItemGroupRequestOptions) => (
        updateCustomItemGroup({
          customItemGroup, customItemGroupParams, dispatch, requestOptions: entityOptions,
        })
      ),
    },
    creating,
    customItemGroup,
    deleting,
    loading: loading && !loadedIds.includes(customItemGroup.id),
    relatedEntity,
    updating,
    urls: generateUrls(customItemGroup),
  }
}

export default useCustomItemGroup
